import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner } from '@w7/ui'
import {
  claims,
  consumeKcActionResult,
  consumeNext,
  handleOidcCallback,
  hasConsoleAccess,
  resolveConsoleHierarchyGate,
} from '@/auth/oidc'
import { flashToast, toastCopy } from '@/ui/toaster'
import './auth.css'

export function CallbackPage() {
  const navigate = useNavigate()
  const [message] = useState('Signing you in…')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const callbackUrl = new URL(location.href)
      const aia = consumeKcActionResult(callbackUrl)
      const result = await handleOidcCallback()
      if (cancelled) return
      history.replaceState({}, '', '/callback')

      if (!result.ok) {
        const reason = result.reason === 'missing-code' ? 'denied' : 'error'
        flashToast(reason === 'denied' ? toastCopy.loginDenied : toastCopy.loginError)
        navigate(`/login?reason=${reason}`, { replace: true })
        return
      }

      const who = claims()
      if (!hasConsoleAccess(who)) {
        const cm = who?.hierarchyLevel === '1'
        navigate(cm ? '/no-access?reason=company-master' : '/no-access', { replace: true })
        return
      }
      // Existing Company Masters may still have `agent` without hierarchyLevel claim.
      if (who?.agentId && !who.hierarchyLevel) {
        const gate = await resolveConsoleHierarchyGate(who.agentId)
        if (gate === 'denied') {
          navigate('/no-access?reason=company-master', { replace: true })
          return
        }
      }

      // Stale Keycloak AIA callbacks (password / MFA) — settings UI is gone; land on Overview.
      if (aia.action === 'UPDATE_PASSWORD') {
        if (aia.status === 'success') flashToast(toastCopy.passwordChanged)
        else if (aia.status === 'cancelled') flashToast(toastCopy.passwordCancelled)
        else flashToast(toastCopy.passwordChangeIncomplete)
        navigate(consumeNext('/overview'), { replace: true })
        return
      }

      if (aia.action) {
        navigate(consumeNext('/overview'), { replace: true })
        return
      }

      flashToast(toastCopy.loginSuccess)
      navigate(consumeNext('/overview'), { replace: true })
    })().catch(() => {
      if (!cancelled) {
        flashToast(toastCopy.loginError)
        navigate('/login?reason=error', { replace: true })
      }
    })
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="w7-auth w7-auth-center" data-testid="auth-callback">
      <Spinner size={22} />
      <p className="w7-dim" style={{ marginTop: 16 }}>
        {message}
      </p>
    </div>
  )
}
